import React, { useEffect, useRef, useState } from "react";
import { MapPin, Pencil, Check, X } from "lucide-react";

// Prominent "where will this archive to?" badge for ticket cards. If
// `onEdit` + `tracks` are provided, clicking the badge opens an inline
// popover to override the track + race before archiving — mirroring the
// Manual-Add form in Ticket History.
export const EditableTargetBadge = ({
  track,
  trackSlug,
  race,
  tracks,
  onEdit,
  testId,
}) => {
  const [open, setOpen] = useState(false);
  const [slug, setSlug] = useState(trackSlug || "");
  const [raceVal, setRaceVal] = useState(race != null ? String(race) : "");
  const popRef = useRef(null);

  // Keep local draft in sync when parent updates (e.g. fresh fetch).
  useEffect(() => setSlug(trackSlug || ""), [trackSlug]);
  useEffect(() => setRaceVal(race != null ? String(race) : ""), [race]);

  // Click-outside to dismiss without saving.
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (popRef.current && !popRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const hasTrack = !!(track && String(track).trim());
  const hasRace = !!(race && String(race).trim());
  const ok = hasTrack && hasRace;
  const editable = typeof onEdit === "function";

  const save = () => {
    const selected = (tracks || []).find((t) => t.slug === slug);
    const name = selected?.name || track || slug || "";
    onEdit?.({
      track_slug: slug,
      name,
      race: raceVal || null,
    });
    setOpen(false);
  };

  return (
    <span className="relative inline-flex" ref={popRef}>
      <button
        type="button"
        onClick={() => editable && setOpen((v) => !v)}
        data-testid={testId}
        disabled={!editable}
        className={`inline-flex items-center gap-1.5 px-2.5 h-7 rounded text-[10px] tracking-[0.22em] uppercase font-bold border transition ${
          editable ? "hover:brightness-125 cursor-pointer" : "cursor-default"
        }`}
        style={{
          fontFamily: "'JetBrains Mono', ui-monospace, monospace",
          background: ok ? "#1c1917" : "#2a1511",
          color: ok ? "#facc15" : "#fca5a5",
          borderColor: ok ? "#78350f" : "#7f1d1d",
        }}
        title={
          editable
            ? ok
              ? `Will archive as "${track} · Race ${race}". Click to edit.`
              : "Click to set the track and race before archiving."
            : ok
              ? `Will archive as "${track} · Race ${race}"`
              : "Set the Track + Race before archiving"
        }
      >
        <MapPin size={11} strokeWidth={2.5} />
        {hasTrack ? track : "Track?"}
        <span className="opacity-40">·</span>
        {hasRace ? `Race ${race}` : "Race?"}
        {editable && <Pencil size={10} strokeWidth={2.5} className="opacity-70 ml-0.5" />}
      </button>

      {open && (
        <div
          data-testid={`${testId}-popover`}
          className="absolute z-50 top-full left-0 mt-1 w-[320px] rounded-md border border-stone-700 bg-[#0a0a0a] p-3 shadow-xl"
          style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace" }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="text-[10px] uppercase tracking-[0.22em] text-stone-500 mb-2">
            Override archive target
          </div>
          <div className="flex flex-col gap-2">
            <div>
              <label className="block text-[9px] uppercase tracking-[0.2em] text-stone-500 mb-1">
                Track
              </label>
              <select
                value={slug}
                onChange={(e) => setSlug(e.target.value)}
                data-testid={`${testId}-track`}
                className="w-full px-2 h-8 rounded bg-[#141414] text-stone-100 text-xs border border-stone-700 focus:border-[#84cc16] focus:outline-none"
                style={{ fontFamily: "inherit" }}
              >
                {(!tracks || tracks.length === 0) && (
                  <option value={slug}>{track || slug || "—"}</option>
                )}
                {(tracks || []).map((t) => (
                  <option key={t.slug} value={t.slug}>
                    {t.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-[9px] uppercase tracking-[0.2em] text-stone-500 mb-1">
                Race
              </label>
              <input
                value={raceVal}
                onChange={(e) =>
                  setRaceVal(e.target.value.replace(/[^\d]/g, ""))
                }
                onKeyDown={(e) => {
                  if (e.key === "Enter") save();
                  if (e.key === "Escape") setOpen(false);
                }}
                data-testid={`${testId}-race`}
                autoFocus
                placeholder="e.g. 3"
                className="w-full px-2 h-8 rounded bg-[#141414] text-stone-100 text-xs border border-stone-700 focus:border-[#84cc16] focus:outline-none"
                style={{ fontFamily: "inherit" }}
              />
            </div>
            <div className="flex items-center justify-end gap-2 pt-1">
              <button
                onClick={() => setOpen(false)}
                className="inline-flex items-center gap-1 px-2 h-7 rounded text-[10px] uppercase tracking-[0.18em] text-stone-400 hover:text-stone-200 border border-stone-700"
                data-testid={`${testId}-cancel`}
              >
                <X size={11} /> Cancel
              </button>
              <button
                onClick={save}
                data-testid={`${testId}-save`}
                className="inline-flex items-center gap-1 px-3 h-7 rounded text-[10px] font-bold uppercase tracking-[0.18em] bg-[#84cc16] text-[#0a0a0a] border border-[#365314] hover:bg-[#a3e635]"
              >
                <Check size={11} /> Set
              </button>
            </div>
          </div>
        </div>
      )}
    </span>
  );
};
